import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileCheck, Euro, BarChart3, Users, Zap, Flame, ChevronRight } from "lucide-react";
import { KpiCard } from "@/components/KpiCard";
import { Badge } from "@/components/Badge";
import { STAGE_COLORS } from "@/lib/constants";
import { getDashboardAffiliato } from "@/api/affiliato";
import type { DashboardAffiliatoResponse } from "@/types";
import { ErrorState } from "@/components/ErrorState";
import { formatMonth } from "@/lib/dates";
import { UltimeNovitaWidget } from "@/components/news/UltimeNovitaWidget";

/** Δ% tra due conteggi mensili. null se non calcolabile (mese precedente a zero). */
function deltaPct(curr: number, prev: number): number | null {
  if (prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

export function DashboardAffiliato() {
  const [data, setData] = useState<DashboardAffiliatoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDashboardAffiliato()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        console.error("Dashboard affiliato error:", e);
        if (!cancelled) setError("Impossibile caricare la dashboard.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded-lg animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-5 space-y-3">
              <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              <div className="h-8 w-16 bg-muted rounded animate-pulse" />
              <div className="h-3 w-32 bg-muted rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="bg-card rounded-xl border border-border p-5 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) {
    return <ErrorState message={error ?? "Errore"} />;
  }

  const prevCount = data.contratti_mese_precedente_count;
  const currCount = data.contratti_mese_count;
  const pct = deltaPct(currCount, prevCount);
  const deltaLabel = pct === null
    ? (prevCount === 0 && currCount > 0 ? "nuovo rispetto al mese prec." : "mese prec. senza dati")
    : `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct)}% vs mese prec.`;
  const deltaColor = pct === null ? "text-muted-foreground" : pct >= 0 ? "text-green-700" : "text-red-600";
  const prevMonthLabel = (() => { const [y, m] = data.month.split("-").map(Number); const d = new Date(y, m - 2, 1); return d.toLocaleDateString("it-IT", { month: "short" }); })();
  const currMonthLabel = (() => { const [y, m] = data.month.split("-").map(Number); const d = new Date(y, m - 1, 1); return d.toLocaleDateString("it-IT", { month: "short" }); })();
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-text">Le mie performance</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{formatMonth(data.month)}</p>
        </div>
        {data.contratti_mese_count > 0 && (
          <span className="text-xs bg-brand/10 text-brand border border-brand/20 px-3 py-1.5 rounded-full font-semibold">
            {data.contratti_mese_count} contratti questo mese
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Contratti mese"
          value={data.contratti_mese_count}
          subtitle={deltaLabel}
          subtitleColor={deltaColor}
          icon={<FileCheck size={16} />}
          accentClass="border-l-brand"
        />
        <KpiCard
          label="Commissioni mese"
          value={`€${data.commissioni_mese_total.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`}
          subtitle="solo contratti attivati"
          icon={<Euro size={16} />}
          accentClass="border-l-amber-400"
        />
        <KpiCard
          label="Contratti totali"
          value={data.contratti_totali_count}
          subtitle="tutti i tempi"
          icon={<BarChart3 size={16} />}
          accentClass="border-l-blue-400"
        />
        <KpiCard
          label="Clienti totali"
          value={data.clienti_count}
          subtitle="visibili al tuo team"
          icon={<Users size={16} />}
          accentClass="border-l-violet-400"
        />
      </div>

      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-text">Andamento contratti</h3>
          <span className={`text-xs font-semibold ${deltaColor}`}>{deltaLabel}</span>
        </div>
        {(() => {
          const max = Math.max(prevCount, currCount, 1);
          const rows = [
            { label: prevMonthLabel, val: prevCount, fill: "bg-muted-foreground/30" },
            { label: currMonthLabel, val: currCount, fill: "bg-brand" },
          ];
          return (
            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.label} className="flex items-center gap-3">
                  <span className="w-12 text-xs font-medium text-muted-foreground capitalize">{r.label}</span>
                  <div className="flex-1 h-6 rounded-lg bg-surface overflow-hidden">
                    <div className={`h-full rounded-lg ${r.fill}`} style={{ width: `${(r.val / max) * 100}%` }} />
                  </div>
                  <span className="w-8 text-right text-sm font-bold text-text tabular-nums font-heading">{r.val}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div data-tour="dashboard-news"><UltimeNovitaWidget /></div>

      <div className="bg-card rounded-xl border border-border p-5">
        <h3 className="font-semibold text-text mb-4">Ultimi contratti</h3>
        {data.ultimi_contratti.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessun contratto recente.</p>
        ) : (
          data.ultimi_contratti.map((c) => {
            const stageColor = STAGE_COLORS[c.stage] ?? STAGE_COLORS["Bozza"];
            const isLuce = c.name.toLowerCase().includes("luce");
            const isGas = c.name.toLowerCase().includes("gas");
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/agenzia/pratiche/${c.id}`)}
                className="flex items-center gap-3 py-3 border-b border-border last:border-0 cursor-pointer hover:bg-brand/5 transition-colors rounded px-2 -mx-2 group"
              >
                <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center flex-shrink-0">
                  {isLuce ? <Zap size={14} className="text-amber-500" /> : isGas ? <Flame size={14} className="text-orange-500" /> : <FileCheck size={14} className="text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.cliente_name ?? "—"}</div>
                </div>
                <Badge bg={stageColor.bg} text={stageColor.text}>{c.stage}</Badge>
                <span className="text-sm font-medium text-text w-20 text-right tabular-nums">
                  {c.quota_agenzia != null ? `€${c.quota_agenzia.toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "—"}
                </span>
                <ChevronRight size={14} className="text-muted-foreground/30 group-hover:text-muted-foreground transition-colors flex-shrink-0" />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
